import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { ControlType, MarkerSizesConfig } from '@/types/floorPlanViewer';
import { useAppStore, useAIToolsState } from '@/stores/appStore';
import { useMarkerSizes } from '@/contexts/MarkerSizeContext';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { ProductImageCallout } from './ProductImageCallout';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import { updateLightingControl } from '@/services/lightingControlService';
import { updateControlRotation } from '@/services/controlService';
import { toast } from 'sonner';
import {
  useMergedCategories,
  MergedCategory,
} from '@/features/product-library/hooks/useMergedCategories';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';
import { useUndoRedoStore } from '@/stores/undoRedoStore';

const CENTRALIZED_KEYPAD_FILL_COLOR = '#1d4ed8';
const CENTRALIZED_KEYPAD_BORDER_COLOR = '#1e3a8a';
const CENTRALIZED_KEYPAD_TEXT_COLOR = '#ffffff';

const getCentralizedKeypadLabel = (sequenceNumber?: number | null) => `K${sequenceNumber || 1}`;

const getCentralizedKeypadFontSize = (label: string, markerSize: number) => {
  if (label.length <= 2) return Math.max(8, markerSize * 0.45);
  if (label.length === 3) return Math.max(7, markerSize * 0.38);
  return Math.max(6, markerSize * 0.3);
};

type DraggedEntityType = 'fixture' | 'control' | 'panel' | 'room';

interface DragState {
  type: DraggedEntityType;
  id: string;
  startPos: { x: number; y: number };
  currentPos: { x: number; y: number };
}

interface ControlLayerProps {
  controls: ControlType[];
  scale: number;
  onStartDrag?: (
    type: DraggedEntityType,
    id: string,
    e: React.MouseEvent | React.TouchEvent | PointerEvent,
    elementPos?: { x: number; y: number },
  ) => void;
  isDragging?: boolean;
  dragState?: DragState | null;
  wasRecentlyDragged?: (elementId: string) => boolean;
  isPublicView?: boolean;
}

export const ControlLayer = memo(
  ({
    controls,
    scale,
    onStartDrag,
    isDragging = false,
    dragState,
    wasRecentlyDragged,
    isPublicView = false,
  }: ControlLayerProps) => {
    const selectedControls = useAppStore(state => state.selection.controls);
    const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
    const hoveredEntityType = useAppStore(state => state.ui.hoveredEntityType);
    const selectEntity = useAppStore(state => state.selectEntity);
    const selectMultipleEntities = useAppStore(state => state.selectMultipleEntities);
    const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
    const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
    const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);
    const setActiveMode = useAppStore(state => state.setActiveMode);
    const viewMode = useAppStore(state => state.ui.viewMode);
    const activeTool = useAppStore(state => state.ui.toolbar.activeTool);
    const showOnlyUnassignedControls = useAppStore(
      state => state.ui.toolbar.showOnlyUnassignedControls,
    );
    const presentationControlId = useAppStore(state => state.ui.presentationControlId);

    // Get AI tools state for opacity
    const aiTools = useAIToolsState();

    // Check if we're in multi-selection mode (more than one entity selected across all types)
    const selectedFixtures = useAppStore(state => state.selection.fixtures);
    const selectedProducts = useAppStore(state => state.selection.products);
    const selectedAnnotations = useAppStore(state => state.selection.annotations);
    const selectedRooms = useAppStore(state => state.selection.rooms);
    const totalSelections =
      selectedFixtures.size +
      selectedControls.size +
      selectedProducts.size +
      selectedAnnotations.size +
      selectedRooms.size;
    const isMultiSelection = totalSelections > 1;

    // Get floor plan data for circuit information
    const { data, temporaryProductLabelFontSize } = useFloorPlanContext();
    const circuitsData = data.circuits || [];

    // Get controls base scale from floor plan metadata (auto-calculated from image dimensions)
    // Default to 1.0 for existing floor plans without auto-scale applied
    const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
    const controlsBaseScale = markerSizes?.controls_base_scale ?? 1.0;
    // Use temporary font size during slider drag, fallback to saved value
    const productLabelFontSize =
      temporaryProductLabelFontSize ?? markerSizes?.product_label_font_size ?? 10;

    // Get circuits that are assigned to selected controls
    const selectedControlCircuits = useMemo(() => {
      const circuits = new Set<string>();
      if (selectedControls.size > 0) {
        controls.forEach(control => {
          if (selectedControls.has(control.id) && control.circuit_id) {
            circuits.add(control.circuit_id);
          }
        });
      }
      return circuits;
    }, [selectedControls, controls]);

    // Get marker size from context
    const { sizes } = useMarkerSizes();
    const rawMarkerSize = sizes.controls;

    // Convert marker size to pixel value
    const getMarkerSizePixels = useCallback((size: number | string): number => {
      if (typeof size === 'number') return size;
      // Convert string size to pixels
      switch (size) {
        case 'small':
          return 16;
        case 'medium':
          return 20;
        case 'large':
          return 24;
        default:
          return 20;
      }
    }, []);

    const markerSizePixels = useMemo(
      () => getMarkerSizePixels(rawMarkerSize),
      [rawMarkerSize, getMarkerSizePixels],
    );

    // Rotation state for controls
    const [rotatingControlId, setRotatingControlId] = useState<string | null>(null);
    const [currentRotationAngle, setCurrentRotationAngle] = useState(0);
    const rotationRef = useRef<{
      startAngle: number;
      startRotation: number;
      lastAngle: number;
      totalDelta: number;
      centerX: number;
      centerY: number;
    } | null>(null);

    // Get undo/redo store for rotation actions
    const addAction = useUndoRedoStore(state => state.addAction);

    // Fetch merged categories (system + org-specific) for database-driven icons
    // This includes organization-specific icons that override system templates
    const { data: controlTemplates = [] } = useMergedCategories('lighting_controls');

    // Helper to get control template - checks in order:
    // 1. Product model number (portal_model) - for per-model icons
    // 2. Product name (portal_name) - for per-product icons
    // 3. Product subcategory (category_info.subcategory) - for type-based icons
    // 4. Control type (keypad, switch, dimmer) - fallback to system templates
    const getControlTemplate = useCallback(
      (controlType: string, categoryInfo?: any): MergedCategory | undefined => {
        // First, try to find by product model number (most specific)
        // This allows per-model icons like "WID-SWB102V2-00"
        if (categoryInfo?.portal_model) {
          const modelTemplate = controlTemplates.find(
            t => t.subcategory === categoryInfo.portal_model,
          );
          if (modelTemplate) {
            return modelTemplate;
          }
        }

        // Second, try by product name (e.g., "Wiss Switch 2-Button")
        if (categoryInfo?.portal_name) {
          const nameTemplate = controlTemplates.find(
            t => t.subcategory === categoryInfo.portal_name,
          );
          if (nameTemplate) {
            return nameTemplate;
          }
        }

        // Third, try by product's subcategory (from category_info)
        // This allows product-type icons set via organization categories
        if (categoryInfo?.subcategory) {
          const productTemplate = controlTemplates.find(
            t => t.subcategory === categoryInfo.subcategory,
          );
          if (productTemplate) {
            return productTemplate;
          }
        }

        // Fall back to control type (keypad, switch, dimmer, etc.)
        return controlTemplates.find(t => t.subcategory === controlType);
      },
      [controlTemplates],
    );

    // Define getControlColor BEFORE getControlIcon since it's used as a dependency
    const getControlColor = useCallback((controlType: string) => {
      switch (controlType) {
        case 'keypad':
          return '#3b82f6'; // Blue-500 for regular keypads
        case 'dimmer':
          return '#fb923c'; // Orange-400
        case 'switch':
          return '#22c55e'; // Green-500
        case 'auxiliary':
          return '#a16207'; // Yellow-700 (brownish)
        case 'centralized-keypad':
        case 'lv-keypad':
          return CENTRALIZED_KEYPAD_BORDER_COLOR;
        default:
          return '#6b7280'; // gray-500
      }
    }, []);

    const getControlIcon = useCallback(
      (control: ControlType, markerSize: number) => {
        // Try to get database-driven icon first
        // Pass category_info to look up product-specific icons from org categories
        const template = getControlTemplate(control.type, control.category_info);
        const isLVKeypad = control.type === 'lv-keypad' || control.type === 'centralized-keypad';

        if (isLVKeypad) {
          const label = getCentralizedKeypadLabel(control.sequence_number);
          const borderWidth = Math.max(2, markerSize * 0.08);

          return (
            <div
              className="flex h-full w-full items-center justify-center font-bold"
              style={{
                backgroundColor: CENTRALIZED_KEYPAD_FILL_COLOR,
                border: `${borderWidth}px solid ${CENTRALIZED_KEYPAD_BORDER_COLOR}`,
                boxSizing: 'border-box',
                color: CENTRALIZED_KEYPAD_TEXT_COLOR,
                fontSize: `${getCentralizedKeypadFontSize(label, markerSize)}px`,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </div>
          );
        }

        // If we have a database template with icon_svg, use CategoryIconRenderer
        if (template?.icon_svg) {
          const iconColor =
            template.fill_color || template.default_color || getControlColor(control.type);

          return (
            <div className="relative flex items-center justify-center h-full w-full">
              <CategoryIconRenderer
                template={{
                  ...template,
                  default_color: iconColor, // Override with computed color
                }}
                size={markerSize * 1.15} // Larger to fill more of container (icon SVGs often have internal padding)
                className="flex-shrink-0"
              />
            </div>
          );
        }

        // Fallback to hardcoded text icons if no database icon
        let displayText = '';

        switch (control.type) {
          case 'keypad':
            displayText = 'K';
            break;
          case 'centralized-keypad':
            // Centralized keypads are LV keypads - use sequence number
            displayText = `K${control.sequence_number || 1}`;
            break;
          case 'lv-keypad':
            // LV keypads - use sequence number
            displayText = `K${control.sequence_number || 1}`;
            break;
          case 'dimmer':
            displayText = 'D';
            break;
          case 'switch':
            displayText = 'S';
            break;
          case 'auxiliary':
            displayText = 'A';
            break;
          default:
            displayText = 'C';
            break;
        }

        // Determine text color based on control type
        const textColor = isLVKeypad ? 'white' : getControlColor(control.type);

        return (
          <div
            className="flex items-center justify-center h-full w-full font-bold"
            style={{
              fontSize: `${Math.max(8, markerSize * 0.4)}px`,
              color: textColor,
            }}
          >
            {displayText}
          </div>
        );
      },
      [getControlTemplate, getControlColor],
    );

    const handleControlClick = useCallback(
      (controlId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        // Don't preventDefault as it can interfere with Safari's event handling

        // Disable all interactions in public view
        if (isPublicView) return;

        // Don't handle clicks if control is being rotated
        const controlElement = e.currentTarget as HTMLElement;
        if (controlElement?.dataset?.rotating === 'true') {
          return;
        }

        // Check if this element was recently dragged
        if (wasRecentlyDragged && wasRecentlyDragged(controlId)) {
          logger.log('Blocked control click - was recently dragged');
          return;
        }

        logger.log(`Control ${controlId} clicked - opening drawer`);

        if (e.shiftKey && selectedControls.size > 0) {
          // Multi-select with shift - add to existing selection
          const newSelection = new Set(selectedControls);
          newSelection.add(controlId);
          selectMultipleEntities('control', Array.from(newSelection));
        } else {
          // Single select - replace selection
          selectEntity('control', controlId);

          // Clear any active placement tools when clicking an element
          setActiveAnnotationType(null);
          setActiveMode('default');
        }

        // Check total selections across all entity types
        const state = useAppStore.getState();
        const totalSelections =
          state.selection.fixtures.size +
          state.selection.controls.size +
          state.selection.products.size +
          state.selection.annotations.size +
          state.selection.rooms.size;

        // Only skip drawer if ALL selections are annotations (they use context toolbar)
        const hasOnlyAnnotations =
          state.selection.annotations.size > 0 &&
          state.selection.annotations.size === totalSelections;

        // Open appropriate drawer based on selection count
        if (totalSelections > 1 && !hasOnlyAnnotations) {
          setActiveDrawer('selection');
        } else {
          setActiveDrawer('control');
        }
      },
      [
        selectedControls,
        selectEntity,
        selectMultipleEntities,
        setActiveDrawer,
        wasRecentlyDragged,
        isPublicView,
      ],
    );

    const handleMouseEnter = useCallback(
      (controlId: string) => {
        // Disable hover effects in public view
        if (!isDragging && !isPublicView) {
          setHoveredEntity(controlId, 'control');
        }
      },
      [setHoveredEntity, isDragging, isPublicView],
    );

    const handleMouseLeave = useCallback(() => {
      // Disable hover effects in public view
      if (!isDragging && !isPublicView) {
        setHoveredEntity(null, null);
      }
    }, [setHoveredEntity, isDragging, isPublicView]);

    const suppressNextMouseDownRef = useRef(false);

    const beginDrag = useCallback(
      (
        controlId: string,
        controlPos: { x: number; y: number },
        e: React.MouseEvent | PointerEvent,
      ) => {
        // Only start drag on left mouse button (or undefined for touch/pen events)
        if ((e as any).button !== 0 && (e as any).button !== undefined) return;

        // Disable dragging in public view
        if (isPublicView) return;

        // Don't preventDefault in mousedown - it prevents click events in Safari!
        // Only stopPropagation to prevent bubbling
        e.stopPropagation();

        // Get current tool from store
        const activeTool = useAppStore.getState().ui.toolbar.activeTool;

        // Only allow dragging when select tool is active
        if (onStartDrag && activeTool === 'select') {
          onStartDrag('control', controlId, e, controlPos);
        }
      },
      [onStartDrag, isPublicView],
    );

    const handleMouseDown = useCallback(
      (controlId: string, controlPos: { x: number; y: number }, e: React.MouseEvent) => {
        if (suppressNextMouseDownRef.current) {
          suppressNextMouseDownRef.current = false;
          return;
        }

        beginDrag(controlId, controlPos, e);
      },
      [beginDrag],
    );

    // Touch event handlers
    const touchTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const touchStartPositions = useRef<Map<string, { x: number; y: number }>>(new Map());

    const handleTouchStart = useCallback(
      (controlId: string, controlPos: { x: number; y: number }, e: React.TouchEvent) => {
        // Only handle single touch
        if (e.touches.length !== 1) return;

        // Don't preventDefault - it prevents the click event from being synthesized
        e.stopPropagation();

        // Disable touch interactions in public view
        if (isPublicView) return;

        const touch = e.touches[0];
        const startPos = { x: touch.clientX, y: touch.clientY };
        touchStartPositions.current.set(controlId, startPos);

        // Set up press-and-hold timer (500ms)
        const timer = setTimeout(() => {
          // Trigger haptic feedback if available
          if ('vibrate' in navigator) {
            navigator.vibrate(10);
          }

          // Start drag mode
          const activeTool = useAppStore.getState().ui.toolbar.activeTool;
          if (onStartDrag && activeTool === 'select') {
            // Create a synthetic mouse event for compatibility with existing drag handler
            const syntheticEvent = {
              preventDefault: () => {},
              stopPropagation: () => {},
              clientX: touch.clientX,
              clientY: touch.clientY,
              button: 0,
              touches: [{ clientX: touch.clientX, clientY: touch.clientY }],
            } as React.TouchEvent;

            onStartDrag('control', controlId, syntheticEvent, controlPos);

            // Remove the timer since we've started dragging
            touchTimers.current.delete(controlId);
          }
        }, 500);

        touchTimers.current.set(controlId, timer);
      },
      [onStartDrag, isPublicView],
    );

    const handleTouchEnd = useCallback(
      (controlId: string, e: React.TouchEvent) => {
        // Don't preventDefault here as it breaks Safari's touch-to-click conversion
        e.stopPropagation();

        // Disable touch interactions in public view
        if (isPublicView) return;

        // Clear the press-and-hold timer
        const timer = touchTimers.current.get(controlId);
        if (timer) {
          clearTimeout(timer);
          touchTimers.current.delete(controlId);

          // If timer was cleared, it means we didn't start dragging, so treat as tap
          const startPos = touchStartPositions.current.get(controlId);
          if (startPos && e.changedTouches.length > 0) {
            const endTouch = e.changedTouches[0];
            const deltaX = Math.abs(endTouch.clientX - startPos.x);
            const deltaY = Math.abs(endTouch.clientY - startPos.y);

            // If finger didn't move much, treat as tap to select
            if (deltaX < 10 && deltaY < 10) {
              // Create synthetic mouse event for click handler
              const syntheticEvent = {
                stopPropagation: () => {},
                preventDefault: () => {},
                button: 0,
                shiftKey: false,
              } as React.MouseEvent;

              handleControlClick(controlId, syntheticEvent);
            }
          }
        }

        touchStartPositions.current.delete(controlId);
      },
      [handleControlClick, isPublicView],
    );

    const handleTouchMove = useCallback((controlId: string, e: React.TouchEvent) => {
      // Cancel press-and-hold timer if finger moves too much
      const timer = touchTimers.current.get(controlId);
      if (timer) {
        const startPos = touchStartPositions.current.get(controlId);
        if (startPos && e.touches.length > 0) {
          const currentTouch = e.touches[0];
          const deltaX = Math.abs(currentTouch.clientX - startPos.x);
          const deltaY = Math.abs(currentTouch.clientY - startPos.y);

          // Cancel timer if moved more than 10 pixels
          if (deltaX > 10 || deltaY > 10) {
            clearTimeout(timer);
            touchTimers.current.delete(controlId);
          }
        }
      }
    }, []);

    // Handle rotation start for controls - matches LVProductLayer pattern
    const handleRotationStart = useCallback(
      (control: ControlType, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        // Get the control element center
        const controlElement = document.querySelector(
          `[data-control-id="${control.id}"]`,
        ) as HTMLElement;
        if (!controlElement) return;

        // Mark element as rotating to prevent click interference
        controlElement.dataset.rotating = 'true';

        const controlRect = controlElement.getBoundingClientRect();
        const centerX = controlRect.left + controlRect.width / 2;
        const centerY = controlRect.top + controlRect.height / 2;

        const startAngle = (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) / Math.PI;

        setRotatingControlId(control.id);
        const initialRotation = control.rotation || 0;
        setCurrentRotationAngle(initialRotation);

        // Initialize rotation tracking with incremental pattern
        rotationRef.current = {
          startAngle,
          startRotation: initialRotation,
          lastAngle: startAngle,
          totalDelta: 0,
          centerX,
          centerY,
        };

        // Store latest rotation in closure - not state (avoids stale closure issue)
        let latestRotation = initialRotation;

        const handleMouseMove = (moveEvent: MouseEvent) => {
          if (!rotationRef.current) return;

          const currentAngle =
            (Math.atan2(
              moveEvent.clientY - rotationRef.current.centerY,
              moveEvent.clientX - rotationRef.current.centerX,
            ) *
              180) /
            Math.PI;

          // Calculate angle difference from last position (incremental)
          let angleDiff = currentAngle - rotationRef.current.lastAngle;

          // Handle wrapping at -180/180 boundary
          if (angleDiff > 180) {
            angleDiff -= 360;
          } else if (angleDiff < -180) {
            angleDiff += 360;
          }

          // Update total delta
          rotationRef.current.totalDelta += angleDiff;
          rotationRef.current.lastAngle = currentAngle;

          // Calculate new rotation
          const newRotation = rotationRef.current.startRotation + rotationRef.current.totalDelta;

          // Store the raw rotation for saving
          let saveRotation = newRotation % 360;
          if (saveRotation < 0) saveRotation += 360;

          // Apply 45-degree snapping by default (hold shift to disable)
          let displayRotation = newRotation;
          if (!moveEvent.shiftKey) {
            const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315];
            const snapThreshold = 5; // degrees

            const normalizedRotation = ((newRotation % 360) + 360) % 360;
            for (const snapAngle of snapAngles) {
              if (Math.abs(normalizedRotation - snapAngle) < snapThreshold) {
                displayRotation =
                  rotationRef.current.startRotation +
                  rotationRef.current.totalDelta -
                  (normalizedRotation - snapAngle);
                saveRotation = snapAngle;
                break;
              }
            }
          }

          // Store in closure variable
          latestRotation = saveRotation;

          // Update state for angle indicator display
          setCurrentRotationAngle(saveRotation);

          // Update visual rotation immediately
          const iconContainer = controlElement.querySelector(
            '[data-icon-container]',
          ) as HTMLElement;
          if (iconContainer) {
            // Preserve current scale from DOM dynamically (includes base scale)
            const currentTransform = iconContainer.style.transform;
            const scaleMatch = currentTransform.match(/scale\(([\d.]+)\)/);
            const currentScale = scaleMatch
              ? parseFloat(scaleMatch[1])
              : (control.scale || 1.0) * controlsBaseScale;
            iconContainer.style.transition = 'none';
            iconContainer.style.transform = `scale(${currentScale}) rotate(${displayRotation}deg)`;
          }
        };

        const handleMouseUp = async () => {
          // Get final rotation from closure - NOT state
          const finalRotation = latestRotation;

          setRotatingControlId(null);
          rotationRef.current = null;

          // Remove rotating flag
          if (controlElement) {
            delete controlElement.dataset.rotating;
          }

          // Ensure visual rotation matches saved value
          const iconContainer = controlElement.querySelector(
            '[data-icon-container]',
          ) as HTMLElement;
          if (iconContainer) {
            // Preserve current scale from DOM dynamically (includes base scale)
            const currentTransform = iconContainer.style.transform;
            const scaleMatch = currentTransform.match(/scale\(([\d.]+)\)/);
            const currentScale = scaleMatch
              ? parseFloat(scaleMatch[1])
              : (control.scale || 1.0) * controlsBaseScale;
            const currentTransition = iconContainer.style.transition;
            iconContainer.style.transition = 'none';
            iconContainer.style.transform = `scale(${currentScale}) rotate(${finalRotation}deg)`;

            // Restore transition after a frame
            requestAnimationFrame(() => {
              iconContainer.style.transition = currentTransition;
            });
          }

          // Save rotation
          try {
            const oldRotation = control.rotation || 0;

            // Only save if rotation actually changed
            if (Math.abs(oldRotation - finalRotation) > 0.1) {
              await updateControlRotation(control.id, finalRotation);

              // Add to undo history
              addAction({
                type: 'ROTATE_ENTITY',
                timestamp: Date.now(),
                floorPlanId: data.floorPlan?.id || '',
                data: {
                  entityType: 'control',
                  id: control.id,
                  oldRotation: oldRotation,
                  newRotation: finalRotation,
                },
              });

              // Delay refetch to prevent visual jumps
              setTimeout(() => {
                data.refetch();
              }, 100);
            }
          } catch (error) {
            logger.error('Failed to save control rotation:', error);
            toast.error('Failed to save rotation');
            // Revert visual rotation on error
            if (iconContainer) {
              iconContainer.style.transition = 'none';
              iconContainer.style.transform = `rotate(${control.rotation || 0}deg)`;
            }
          }

          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      },
      [data, addAction], // Note: removed currentRotationAngle dependency - uses closure instead
    );

    return (
      <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 15 }}>
        {controls.map(control => {
          // Apply unassigned controls filter
          if (showOnlyUnassignedControls && control.circuit_id) {
            return null;
          }

          const isSelected = selectedControls.has(control.id);
          const isHovered = hoveredEntityId === control.id;
          const isBeingDragged = dragState?.type === 'control' && dragState?.id === control.id;
          const isPresentationSelected = presentationControlId === control.id;

          // Find circuit data for badge
          const circuit = control.circuit_id
            ? circuitsData.find(c => c.id === control.circuit_id)
            : null;

          // Check if this control belongs to a circuit being hovered in a drawer
          // Only apply hover effect in single selection mode (not during multi-selection)
          const isInHoveredCircuit =
            hoveredEntityType === 'circuit' &&
            hoveredEntityId &&
            control.circuit_id === hoveredEntityId &&
            !isMultiSelection;

          // Calculate badge position proportionally to marker size
          const badgeOffset = markerSizePixels * 0.35; // 35% of marker size for proportional scaling

          return (
            <div
              key={control.id}
              data-control-id={control.id}
              data-width={control.width || markerSizePixels}
              data-height={control.height || markerSizePixels}
              className={cn(
                'absolute transition-all duration-200 rounded-sm',
                'flex items-center justify-center select-none',
                {
                  'cursor-pointer': !isPublicView,
                  'cursor-grab':
                    !isDragging && !isBeingDragged && activeTool === 'select' && !isPublicView,
                  'cursor-grabbing': isBeingDragged && !isPublicView,
                  'shadow-lg z-[100]': isBeingDragged,
                  'opacity-50': isDragging && !isBeingDragged,
                },
              )}
              style={{
                left: `${control.x}px`,
                top: `${control.y}px`,
                width: `${markerSizePixels}px`,
                height: `${markerSizePixels}px`,
                pointerEvents: 'auto',
                zIndex: isSelected ? 100 : isHovered ? 50 : isBeingDragged ? 200 : 15,
                transform: 'translate(-50%, -50%)',
                // Dynamic selection outline that stays outside scaled icon
                ...(isSelected && {
                  outline: '1px solid #9ca3af', // gray-400
                  outlineOffset: `${(((control.scale || 1.0) * controlsBaseScale - 1) * markerSizePixels) / 2}px`,
                }),
              }}
              onMouseEnter={() => handleMouseEnter(control.id)}
              onMouseLeave={handleMouseLeave}
              onMouseDown={e => handleMouseDown(control.id, { x: control.x, y: control.y }, e)}
              onClick={e => handleControlClick(control.id, e)}
              onPointerDown={e => {
                // Safety check for event object and button property
                if (!e) return;

                // For Safari, handle selection directly in pointerDown
                // Check button is 0 (left click) or undefined (touch/pen events)
                if (e.pointerType === 'mouse' && (e.button === 0 || e.button === undefined)) {
                  suppressNextMouseDownRef.current = true;
                  window.setTimeout(() => {
                    suppressNextMouseDownRef.current = false;
                  }, 0);

                  const isSafari =
                    navigator.userAgent.includes('Safari') &&
                    !navigator.userAgent.includes('Chrome');

                  if (!wasRecentlyDragged?.(control.id)) {
                    const startX = e.clientX;
                    const startY = e.clientY;
                    let didStartDrag = false;
                    const pointerDownEvent = e.nativeEvent;

                    const handlePointerMove = (moveEvent: PointerEvent) => {
                      const deltaX = Math.abs(moveEvent.clientX - startX);
                      const deltaY = Math.abs(moveEvent.clientY - startY);

                      // If moved more than 5px, it's a drag
                      if (deltaX > 5 || deltaY > 5) {
                        didStartDrag = true;
                        beginDrag(control.id, { x: control.x, y: control.y }, pointerDownEvent);
                        // Clean up listeners
                        document.removeEventListener('pointermove', handlePointerMove);
                        document.removeEventListener('pointerup', handlePointerUp);
                        document.removeEventListener('pointercancel', handlePointerUp);
                      }
                    };

                    const handlePointerUp = (upEvent: PointerEvent) => {
                      document.removeEventListener('pointermove', handlePointerMove);
                      document.removeEventListener('pointerup', handlePointerUp);
                      document.removeEventListener('pointercancel', handlePointerUp);

                      if (isSafari && !didStartDrag) {
                        handleControlClick(control.id, upEvent as any);
                      }
                    };

                    document.addEventListener('pointermove', handlePointerMove);
                    document.addEventListener('pointerup', handlePointerUp);
                    document.addEventListener('pointercancel', handlePointerUp);

                    // Clean up listeners after 1 second in case events never fire
                    setTimeout(() => {
                      document.removeEventListener('pointermove', handlePointerMove);
                      document.removeEventListener('pointerup', handlePointerUp);
                      document.removeEventListener('pointercancel', handlePointerUp);
                    }, 1000);
                  }
                }
              }}
              onTouchStart={e => handleTouchStart(control.id, { x: control.x, y: control.y }, e)}
              onTouchEnd={e => handleTouchEnd(control.id, e)}
              onTouchMove={e => handleTouchMove(control.id, e)}
              onContextMenu={e => e.preventDefault()}
            >
              {/* Icon container with scale and rotation */}
              {(() => {
                const baseEffectiveScale = (control.scale || 1.0) * controlsBaseScale;
                const rotation =
                  rotatingControlId === control.id ? currentRotationAngle : control.rotation || 0;

                // Read visual scale from DOM during slider drag (when DOM scale differs from React state)
                // This ensures badge position is consistent during drag AND after save
                let effectiveControlScale = baseEffectiveScale;
                if (typeof document !== 'undefined') {
                  const iconEl = document.querySelector(
                    `[data-control-id="${control.id}"] [data-icon-container]`,
                  ) as HTMLElement;
                  if (iconEl?.style.transform) {
                    const match = iconEl.style.transform.match(/scale\(([\d.]+)\)/);
                    if (match) {
                      effectiveControlScale = parseFloat(match[1]);
                    }
                  }
                }

                return (
                  <div
                    data-icon-container
                    style={{
                      transform: `scale(${baseEffectiveScale}) rotate(${rotation}deg)`,
                      transition: rotatingControlId === control.id ? 'none' : 'transform 0.2s',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {getControlIcon(control, markerSizePixels)}

                    {/* Circuit Badge - INSIDE scaled container for automatic positioning */}
                    {circuit && (
                      <div
                        className={cn('absolute rounded-full font-medium pointer-events-none', {
                          // Enhanced styling when circuit is hovered
                          'bg-blue-50 border border-blue-400 text-blue-600 animate-slow-blink shadow-md':
                            isInHoveredCircuit,
                          // White background, subtle border - different from K1/K2 badges
                          'bg-white border border-gray-300 text-gray-500 shadow-sm':
                            !isInHoveredCircuit,
                        })}
                        style={{
                          // Counter-scale offset to maintain consistent visual distance from icon corner
                          // offset * scale = constant visual distance (~8px from corner)
                          right: `${-8 / effectiveControlScale}px`,
                          top: `${-8 / effectiveControlScale}px`,
                          fontSize: '11px',
                          padding: '2px 5px',
                          minWidth: '18px',
                          minHeight: '18px',
                          whiteSpace: 'nowrap',
                          lineHeight: '1.2',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: isInHoveredCircuit ? 2000 : 1000,
                          // Counter-scale to keep badge readable, counter-rotate to keep text upright
                          transform: `scale(${(isInHoveredCircuit ? 1.1 : 1) / effectiveControlScale}) rotate(${-rotation}deg)`,
                          transformOrigin: 'center',
                          transition:
                            rotatingControlId === control.id ? 'none' : 'all 0.2s ease-in-out',
                        }}
                        data-control-badge-id={control.id}
                      >
                        {circuit.sequence_number || circuit.name || 'C'}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Product Model Label - show when control has assigned product */}
              {control.product_id && (
                <ControlProductLabel
                  productId={control.product_id}
                  controlId={control.id}
                  controlScale={control.scale || 1.0}
                  controlsBaseScale={controlsBaseScale}
                  labelFontSize={productLabelFontSize}
                />
              )}

              {/* Rotation handle for selected controls */}
              {isSelected &&
                !isPublicView &&
                !isBeingDragged &&
                (() => {
                  // Calculate scale-aware position for rotation handle
                  const effectiveControlScale = (control.scale || 1.0) * controlsBaseScale;
                  const iconRadius = 24;
                  const margin = 11;
                  const calculatedOffset = iconRadius * effectiveControlScale + margin;
                  const rotationOffset = Math.max(calculatedOffset, 35);

                  return (
                    <div
                      className="absolute"
                      data-rotation-handle="true"
                      data-control-rotation-handle={control.id}
                      style={{
                        left: '50%',
                        top: `-${rotationOffset}px`,
                        transform: 'translateX(-50%)',
                        pointerEvents: 'auto',
                        zIndex: 1000,
                      }}
                    >
                      {/* Rotation handle button */}
                      <div
                        className="bg-white rounded-full shadow-lg border border-blue-400/80 cursor-grab hover:scale-110 hover:border-blue-500 hover:shadow-xl transition-all flex items-center justify-center"
                        style={{
                          width: '18px',
                          height: '18px',
                        }}
                        onMouseDown={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRotationStart(control, e);
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onPointerDown={e => {
                          e.stopPropagation();
                        }}
                      >
                        {/* Rotation icon - elegant curved arrow */}
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-blue-500"
                        >
                          <path
                            d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.0902 3 17.7902 4.58073 19.3499 7"
                            stroke="currentColor"
                            strokeWidth={1.8}
                            strokeLinecap="round"
                          />
                          <path
                            d="M17 7L19.5 7L19.5 4.5"
                            stroke="currentColor"
                            strokeWidth={1.8}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      {/* Angle indicator when rotating */}
                      {rotatingControlId === control.id && (
                        <div
                          className="absolute px-2 py-1 rounded text-xs"
                          style={{
                            backgroundColor: 'rgba(37, 99, 235, 0.9)',
                            whiteSpace: 'nowrap',
                            left: '30px',
                            top: '-3px',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '10px',
                          }}
                        >
                          {Math.round(currentRotationAngle)}°
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* Show blinking border when circuit is hovered (only in single selection mode) */}
              {isInHoveredCircuit && (
                <div
                  className="absolute -inset-1 border-2 border-red-500 border-dashed animate-slow-blink pointer-events-none"
                  style={{ borderRadius: '2px' }}
                />
              )}

              {/* Show dragging indicator */}
              {isBeingDragged && (
                <div className="absolute -inset-2 border-2 border-green-500 border-dashed rounded animate-pulse" />
              )}

              {/* Show presentation mode selection indicator - pulse ring effect */}
              {isPresentationSelected &&
                (() => {
                  // Calculate dynamic inset based on scale to position rings outside the icon
                  const effectiveScale = (control.scale || 1.0) * controlsBaseScale;
                  const overflow = ((effectiveScale - 1) * markerSizePixels) / 2;
                  return (
                    <>
                      {/* Primary ring - animated pulse outside scaled icon */}
                      <div
                        className="absolute border-2 border-primary rounded pointer-events-none animate-pulse-ring"
                        style={{ inset: `-${overflow + 4}px` }}
                      />
                      {/* Secondary ring - delayed pulse */}
                      <div
                        className="absolute border-2 border-primary rounded pointer-events-none animate-pulse-ring"
                        style={{ inset: `-${overflow + 4}px`, animationDelay: '0.5s' }}
                      />
                      {/* Static border for visibility */}
                      <div
                        className="absolute border-2 border-primary/60 rounded pointer-events-none"
                        style={{ inset: `-${overflow + 2}px` }}
                      />
                    </>
                  );
                })()}
            </div>
          );
        })}

        {/* Render product image callouts for controls with show_product_image enabled */}
        {controls
          .filter(control => control.show_product_image && control.product_id)
          .map(control => (
            <ControlCalloutWrapper
              key={`callout-${control.id}`}
              control={control}
              scale={scale}
              isSelected={selectedControls.has(control.id)}
              isPublicView={isPublicView}
              onRefetch={data.refetch}
              dragState={dragState}
            />
          ))}
      </div>
    );
  },
);

ControlLayer.displayName = 'ControlLayer';

/**
 * Wrapper component to render a control product image callout
 */
const ControlCalloutWrapper = memo(
  ({
    control,
    scale,
    isSelected,
    isPublicView,
    onRefetch,
    dragState,
  }: {
    control: ControlType;
    scale: number;
    isSelected: boolean;
    isPublicView: boolean;
    onRefetch: () => void;
    dragState?: DragState | null;
  }) => {
    const { data: linkedProduct } = useProduct(control.product_id || undefined);

    // Get image URL from various sources
    const imageUrl =
      linkedProduct?.image_url ||
      (linkedProduct?.portal_metadata as any)?.imageUrl ||
      (control.category_info as any)?.portal_image_url;

    if (!imageUrl) {
      return null;
    }

    // Calculate live icon position if this control is being dragged
    const isBeingDragged = dragState?.type === 'control' && dragState.id === control.id;
    const iconPosition =
      isBeingDragged && dragState?.currentPos
        ? { x: dragState.currentPos.x, y: dragState.currentPos.y }
        : undefined;

    // Adapt control to LVProduct-like shape for ProductImageCallout
    const productLike = {
      id: control.id,
      name: control.name,
      x_position: control.x,
      y_position: control.y,
      product_image_x: control.product_image_x,
      product_image_y: control.product_image_y,
      product_image_width: control.product_image_width,
      product_image_height: control.product_image_height,
    };

    const handlePositionChange = async (x: number, y: number) => {
      try {
        await updateLightingControl(control.id, {
          productImageX: x,
          productImageY: y,
        });
        onRefetch();
      } catch (error) {
        logger.error('Failed to update image position:', error);
        toast.error('Failed to update image position');
      }
    };

    const handleSizeChange = async (width: number, height: number) => {
      try {
        await updateLightingControl(control.id, {
          productImageWidth: width,
          productImageHeight: height,
        });
        onRefetch();
      } catch (error) {
        logger.error('Failed to update image size:', error);
        toast.error('Failed to update image size');
      }
    };

    const handleClose = async () => {
      try {
        await updateLightingControl(control.id, {
          showProductImage: false,
        });
        onRefetch();
      } catch (error) {
        logger.error('Failed to hide image callout:', error);
        toast.error('Failed to hide image callout');
      }
    };

    return (
      <ProductImageCallout
        product={productLike as any}
        imageUrl={imageUrl}
        scale={scale}
        isSelected={isSelected}
        isPublicView={isPublicView}
        onPositionChange={handlePositionChange}
        onSizeChange={handleSizeChange}
        onClose={handleClose}
        iconPosition={iconPosition}
      />
    );
  },
);

ControlCalloutWrapper.displayName = 'ControlCalloutWrapper';

// Component to display product model label below control icon
const ControlProductLabel = memo(
  ({
    productId,
    controlId,
    controlScale,
    controlsBaseScale,
    labelFontSize = 10,
  }: {
    productId: string;
    controlId: string;
    controlScale: number;
    controlsBaseScale: number;
    labelFontSize?: number;
  }) => {
    const { data: product } = useProduct(productId);

    if (!product?.model) {
      return null;
    }

    // Get current scale - read from DOM for live updates during slider drag
    let currentControlScale = controlScale * controlsBaseScale;
    const iconContainer = document.querySelector(
      `[data-control-id="${controlId}"] [data-icon-container]`,
    ) as HTMLElement;
    if (iconContainer && iconContainer.style.transform) {
      const scaleMatch = iconContainer.style.transform.match(/scale\(([\d.]+)\)/);
      if (scaleMatch) {
        currentControlScale = parseFloat(scaleMatch[1]);
      }
    }

    // Label moves down proportionally with scale (same formula as LV products)
    const labelMarginTop = 6 + (currentControlScale - 1.0) * 10;

    return (
      <div
        data-control-label={controlId}
        className="absolute left-0 top-full text-xs font-medium whitespace-nowrap pointer-events-none text-gray-700"
        style={{
          fontSize: `${labelFontSize}px`,
          marginTop: `${labelMarginTop}px`,
          transform: 'translateX(-50%)',
          transformOrigin: 'top center',
          left: '50%',
        }}
      >
        {product.model}
      </div>
    );
  },
);

ControlProductLabel.displayName = 'ControlProductLabel';
